import { RestHandler, MockedRequest, DefaultRequestBody, rest } from 'msw';
import { DocumentRecordRequest } from '../../queries/use-create-document';
import { CreateLinksRequest } from '../../queries/use-create-links';
import { CreateReferencesRequest } from '../../queries/use-create-references';
import { DeleteDocumentsRequest } from '../../queries/use-delete-documents';
import { ReplacePresignedPutUrlRequest } from '../../queries/use-replace-document';
import { UpdateFileRecordsRequest } from '../../queries/use-update-file-records';
import { PostResponseBody } from '../../types/api-response';
import { FileRecord, FileRecordOverview, FileRecordTypeEnum, FileStatusEnum } from '../../types/file-record';
import { DB } from '../db';
import { paginate } from '../utils';

const validMimeTypes = [
    'text/plain',
    'text/csv',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/jpeg',
    'image/png',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'image/svg+xml',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

const routes: RestHandler<MockedRequest<DefaultRequestBody>>[] = [];

routes.push(
    /**
     * GET /files-records
     * Gets all files for a folder
     */
    rest.get('/file-records', (req, res, ctx) => {
        const folderId = req.url.searchParams.get('folderId') || undefined;
        const childId = req.url.searchParams.get('childId') || undefined;
        const page = req.url.searchParams.get('page') as unknown as number;
        const limit = req.url.searchParams.get('limit') as unknown as number;

        const records = DB.GetFileRecords(childId, folderId);
        const path = DB.GetFilePath(folderId);

        return res(
            ctx.delay(),
            ctx.status(200),
            ctx.json({
                data: {
                    records: paginate<FileRecordOverview>(records, page, limit),
                    path,
                },
                totalItems: records.length,
                count: paginate<FileRecordOverview>(records, page, limit).length,
            })
        );
    }),

    /**
     * POST /file-records/upload/presigned-put-url
     * Generate an s3 presigned url for each provided file
     */
    // rest.post<PresignedPutUrlRequest>('/file-records/upload/presigned-put-url', (req, res, ctx) => {
    //     return res(ctx.delay(), ctx.status(200), ctx.json({ url: `https://wp-mock-bucket.s3.amazonaws.com/${req.body.fileName}` }));
    // }),

    /**
     * GET /file-records/upload/presigned-get-url
     * Generate an s3 presigned url for each provided file
     */
    rest.get('/file-records/documents/download', (req, res, ctx) => {
        const recordIds = req.url.searchParams.getAll('recordIds[]');
        const urls: { recordId: string; url: string }[] = [];
        for (const recordId of recordIds) {
            const record = DB.GetFileRecord(recordId);
            if (record) {
                let originalId = record.id;
                DB.UpdateFileRecord(record.id, { status: FileStatusEnum.CHECKED_OUT, updatedAt: new Date() });
                DB.AddFileRecordHistory(record.id, { message: `${record.name} was checked out.`, createdAt: new Date() });
                const references = DB.GetReferences(record.id);
                references.forEach((ref) => {
                    originalId = ref.source?.id as string;
                    DB.UpdateFileRecord(ref.id, { status: FileStatusEnum.CHECKED_OUT, updatedAt: new Date() });
                    DB.AddFileRecordHistory(ref.id, { message: `${record.name} was checked out.`, createdAt: new Date() });
                });
                urls.push({ recordId, url: `${window.location.origin}/wp-mock-bucket.s3.amazonaws.com/${originalId}` });
            }
        }

        return res(ctx.delay(), ctx.status(200), ctx.json({ urls }));
    }),

    /**
     * POST /file-records/upload/presigned-put-url/replace
     * Generate an s3 presigned url for each provided file to replace the current file in S3
     */
    rest.get<ReplacePresignedPutUrlRequest>('/file-records/documents/:id/replace', (req, res, ctx) => {
        return res(ctx.delay(), ctx.status(200), ctx.json({ url: `https://wp-mock-bucket.s3.amazonaws.com/${req.params.id}` }));
    }),

    /**
     * GET /file-records/:id
     * Gets a specific file by its id
     */
    rest.get('/file-records/:id', (req, res, ctx) => {
        const id = req.params.id as string;
        const file = DB.GetFileRecord(id);

        return res(ctx.delay(), ctx.status(file ? 200 : 404), ctx.json({ file }));
    }),

    /**
     * POST /file-records/documents
     * Create a document record
     */
    rest.post<DocumentRecordRequest>('/file-records/documents', (req, res, ctx) => {
        const { name, size, type, parentFolderId } = req.body;

        let recordName = name;

        const records = DB.GetFileRecords(undefined, parentFolderId);

        const existingRecord = records.find((r) => r.name.toLowerCase() === recordName.toLowerCase());

        // Keep checking for a duplicate file name
        if (existingRecord) {
            let i = 1;
            while (records.find((r) => r.name.toLowerCase() === `${recordName.toLowerCase()} (${i})`)) {
                i += 1;
            }
            recordName = `${recordName} (${i})`;
        }

        const record = DB.CreateDocumentRecord(recordName, type, size, parentFolderId);

        return res(
            ctx.delay(),
            ctx.status(200),
            ctx.json({ record, upload: { url: `https://wp-mock-bucket.s3.amazonaws.com/${record.id}`, key: record.id } })
        );
    }),

    /**
     * POST /file-records/links
     * Create a link record
     */
    rest.post<CreateLinksRequest>('/file-records/links', (req, res, ctx) => {
        const requests = req.body;

        const response: PostResponseBody<FileRecord> = {
            hasErrors: false,
            results: [],
        };

        for (const req of requests) {
            try {
                const reference = DB.CreateLinkRecord(req.name, req.link, req.parentFolderId ? [req.parentFolderId] : []);
                response.results.push({ statusCode: 201, data: reference });
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (error: any) {
                response.hasErrors = true;
                response.results.push({
                    statusCode: 400,
                    error: {
                        original: { name: req.name, link: req.link, parentFolderId: req.parentFolderId },
                        fields: { name: error.message },
                    },
                });
            }
        }
        return res(ctx.delay(), ctx.status(response.hasErrors ? 400 : 201), ctx.json(response));
    }),

    /**
     * POST /file-records/references
     * Create a reference record
     */
    rest.post<CreateReferencesRequest>('/file-records/references', (req, res, ctx) => {
        const requests = req.body;

        const response: PostResponseBody<FileRecord> = {
            hasErrors: false,
            results: [],
        };

        for (const req of requests) {
            try {
                const reference = DB.CreateReferenceRecord(req.name, req.sourceFileId, req.parentFolderId);
                response.results.push({ statusCode: 201, data: reference });
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (error: any) {
                response.hasErrors = true;
                response.results.push({
                    statusCode: 400,
                    error: {
                        original: { name: req.name, sourceFileId: req.sourceFileId, parentFolderId: req.parentFolderId },
                        fields: { name: error.message },
                    },
                });
            }
        }

        return res(ctx.delay(), ctx.status(response.hasErrors ? 400 : 201), ctx.json(response));
    }),
    /**
     * DELETE /file-records/documents
     * Delete document records
     */
    rest.delete<DeleteDocumentsRequest>('/file-records', (req, res, ctx) => {
        const deleteRequest = req.body;

        for (const req of deleteRequest) {
            const fileRecord = DB.GetFileRecord(req.id);
            if (!fileRecord) {
                continue;
            }
            const references = DB.GetAllFileRecords().filter((r) => r?.source?.id === req.id);

            if (!req.keepReferences) {
                // Delete all references
                for (const ref of references) {
                    DB.DeleteFileRecord(ref.id);
                }
            } else {
                for (const ref of references) {
                    const refRecord = DB.GetFileRecord(ref.id) as FileRecord;
                    DB.UpdateFileRecord(ref.id, {
                        bytes: fileRecord.bytes,
                        mimeType: fileRecord.mimeType,
                        updatedAt: new Date(),
                        createdAt: refRecord.createdAt,
                        recordType:
                            fileRecord.recordType === FileRecordTypeEnum.DOCUMENT ? FileRecordTypeEnum.DOCUMENT : FileRecordTypeEnum.LINK,
                        source: undefined,
                        link: fileRecord.link,
                        status:
                            fileRecord.recordType === FileRecordTypeEnum.DOCUMENT
                                ? FileStatusEnum.CHECKED_IN
                                : FileStatusEnum.NOT_APPLICABLE,
                    });
                    DB.AddFileRecordHistory(ref.id, {
                        createdAt: new Date(),
                        message: `Record type changed from reference to document due to the original file (${fileRecord.name}) being deleted.`,
                    });
                }
            }
            DB.DeleteFileRecord(req.id);
        }

        return res(ctx.delay(), ctx.status(204));
    }),

    /**
     * PATCH /file-records
     * Update file records
     */
    rest.patch<UpdateFileRecordsRequest>('/file-records', (req, res, ctx) => {
        const request = req.body;

        const response: PostResponseBody<FileRecord> = {
            hasErrors: false,
            results: [],
        };

        for (const req of request) {
            const record = DB.GetFileRecord(req.id);

            if (!record) {
                continue;
            }

            // Update the link
            if (record.recordType === FileRecordTypeEnum.LINK && req.fields.link) {
                DB.UpdateFileRecord(record.id, { link: req.fields.link, updatedAt: new Date() });
            }

            // Name change
            if (record.name !== req.fields.name && req.fields.name !== undefined) {
                try {
                    const duplicateName = DB.GetAllFileRecords()
                        .filter((f) => f.parentFolderId?.at(0) === record.parentFolderId?.at(0))
                        .some((f) => f.name.toLowerCase() === req.fields.name?.toLowerCase());

                    if (duplicateName) {
                        throw new Error('A record with this name already exists.');
                    }
                    const updatedRecord = DB.UpdateFileRecord(record.id, { name: req.fields.name, updatedAt: new Date() });
                    DB.AddFileRecordHistory(record.id, { createdAt: new Date(), message: `Renamed ${record.name} to ${req.fields.name}` });

                    response.results.push({ statusCode: 201, data: updatedRecord as FileRecord });
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } catch (error: any) {
                    response.hasErrors = true;
                    response.results.push({
                        statusCode: 400,
                        error: {
                            original: { ...req },
                            fields: { name: error.message },
                        },
                    });
                }
            }

            if (req.fields.status) {
                DB.UpdateFileRecord(record.id, { status: req.fields.status });
            }

            if (req.updateReferences) {
                const references = DB.GetReferences(record.id);

                for (const ref of references) {
                    if (req.fields.name) {
                        const refName = DB.EnsureUniqueFileRecordName(req.fields.name as string, ref.parentFolderId?.at(0) as string);
                        DB.UpdateFileRecord(ref.id, {
                            name: refName,
                            updatedAt: new Date(),
                        });
                        DB.AddFileRecordHistory(ref.id, { createdAt: new Date(), message: `Renamed ${ref.name} to ${refName}` });
                    }

                    if (req.fields.status) {
                        DB.UpdateFileRecord(ref.id, {
                            status: req.fields.status,
                            updatedAt: new Date(),
                        });
                        DB.AddFileRecordHistory(ref.id, { createdAt: new Date(), message: `Marked ${ref.name} ad ${req.fields.status}` });
                    }
                }
            }

            // Parent id change
            if (record.parentFolderId?.at(0) !== req.fields.parentFolderId && req.fields.parentFolderId !== undefined) {
                const parentIdFolder = req.fields.parentFolderId as string;
                const parentFolder = DB.GetFileRecord(parentIdFolder);
                const uniqueName = DB.EnsureUniqueFileRecordName(record.name, parentIdFolder);

                DB.UpdateFileRecord(record.id, { parentFolderId: [parentIdFolder], name: uniqueName, updatedAt: new Date() });
                DB.AddFileRecordHistory(req.id, { createdAt: new Date(), message: `${record.name} moved to ${parentFolder?.name}.` });
            }

            if (record.bytes !== req.fields.bytes && req.fields.bytes) {
                DB.UpdateFileRecord(record.id, { bytes: req.fields.bytes, updatedAt: new Date() });
            }

            if (record.mimeType !== req.fields.mimeType && req.fields.mimeType) {
                DB.UpdateFileRecord(record.id, { mimeType: req.fields.mimeType, updatedAt: new Date() });
            }

            if (
                (record.bytes !== req.fields.bytes && req.fields.bytes) ||
                (record.mimeType !== req.fields.mimeType && req.fields.mimeType)
            ) {
                DB.AddFileRecordHistory(req.id, { createdAt: new Date(), message: `${record.name} was replaced.` });
            }
        }

        return res(ctx.delay(), ctx.status(response.hasErrors ? 400 : 200), ctx.json(response));
    }),

    /**
     * PUT https://wp-mock-bucket.s3.amazonaws.com/*
     * Intercept any s3 call and save locally intsead
     */
    rest.put<{ [key: string]: File }>('https://wp-mock-bucket.s3.amazonaws.com/:name', (req, res, ctx) => {
        const name = req.params.name as string;

        const document = req.body[name];

        return res(ctx.delay(1000), ctx.status(200), ctx.json({}));
    }),

    /**
     * GET https://wp-mock-bucket.s3.amazonaws.com/*
     * Intercept any s3 call and save locally intsead
     */
    rest.get<{ [key: string]: File }>(`${window.location.origin}/wp-mock-bucket.s3.amazonaws.com/:id`, async (req, res, ctx) => {
        const id = req.params.id as string;
        const _res = await fetch('/example.pdf');
        const blob = await _res.blob();
        const buffer = await blob.arrayBuffer();

        // Mark the document as checked out

        return res(
            ctx.delay(1000),
            ctx.status(200),
            ctx.set('Content-Length', buffer.byteLength.toString()),
            ctx.set('Content-Type', 'application/pdf'),
            ctx.body(blob)
        );
    }),

    /**
     * GET /file-records/documents/view
     * Generates a presigned get url to view a pdf
     */
    rest.get('/file-records/documents/view', (req, res, ctx) => {
        const id = req.url.searchParams.get('recordId') as string;

        let url = '';
        let record = DB.GetFileRecord(id) as FileRecord;
        if (!record) {
            return res(ctx.delay(), ctx.status(200), ctx.json({ url: `${window.location.origin}/wp-mock-bucket.s3.amazonaws.com/${id}` }));
        }
        if (record.recordType === FileRecordTypeEnum.REFERENCE) {
            record = DB.GetFileRecord(record.source?.id as string) as FileRecord;
        }

        if (record && record.recordType === FileRecordTypeEnum.DOCUMENT) {
            const mimeType = record.mimeType as string;

            // Don't need to do anything special
            if (mimeType === 'application/pdf') {
                url = `${window.location.origin}/wp-mock-bucket.s3.amazonaws.com/${id}`;
            }
            if (validMimeTypes.includes(mimeType)) {
                url = `${window.location.origin}/wp-mock-bucket.s3.amazonaws.com/${id}`;
            }
        }

        return res(ctx.delay(), ctx.status(url ? 200 : 422), ctx.json({ url }));
    })
);

export { routes };
